import {
  Component,
  Element,
  Event,
  EventEmitter,
  State,
  Prop,
  Watch,
  h,
  ComponentInterface,
} from '@stencil/core';
import type { ResizeStateChangeEventDetail } from '../../interface';

/**
 * Img with benefits. Lazy loading. Simple `srcSet` solution for both `<img>` and `background-image` varieties.
 * @slot - Main slot. Only relevant with `<nano-img background>`
 */
@Component({
  tag: 'nano-img',
  styleUrl: 'img.scss',
  shadow: true,
})
export class Img implements ComponentInterface {
  private io?: IntersectionObserver;
  private _srcSet: { [key: string]: { src: string; active: boolean } } = {};

  @Element() host!: HTMLNanoImgElement;

  @State() loadSrc?: string;
  @State() loadError?: () => void;
  @State() hasLoaded: boolean = false;
  @State() imgStates: string = null;
  @State() _src: string;
  @Watch('_src')
  _srcChanged() {
    this.hasLoaded = false;
    this.addIO();
  }

  /** This attribute defines the alternative text describing the image.
   * Users will see this text displayed if the image URL is wrong, or if the image is not yet downloaded. */
  @Prop() alt?: string;

  /** The image URL. This attribute is mandatory for the `<img>` element. */
  @Prop() src!: string;
  @Watch('src')
  srcChanged() {
    this._src = this.src;
  }

  /** Optional list string providing media sizes with corresponding image srcs.
   * i.e. show img-x at 300px wide. Format `srcSet="200w src/imgSmall.jpg, 500h src/imgMed.png"` */
  @Prop() srcSet?: string;
  @Watch('srcSet')
  srcSetChanged() {
    if (!this.srcSet) return;

    delete this._srcSet;
    this._srcSet = {};

    this.imgStates = this.srcSet
      .split(',')
      .map((bpSrc) => {
        const [bp, src] = bpSrc.split(' ').filter((bp) => bp.length);
        this._srcSet[bp] = { src: src, active: false };
        return bp;
      })
      .join(', ');
  }

  /** Render image lazily, when it comes into the browser viewport **/
  @Prop() lazy: boolean = true;
  @Watch('lazy')
  lazyChanged() {
    if (!this.lazy) this.load();
  }

  /** Render the image as a background image **/
  @Prop() background?: boolean;

  /** When the image is rendered as a background, and there is no preset dimensions of the component,
   * you can set how the component height will be set. Via image size or via the content **/
  @Prop() autoHeight: 'content' | 'image' = 'content';

  /** Emitted when the img src has been set */
  @Event() nanoImgWillLoad!: EventEmitter<void>;

  /** Emitted when the image has finished loading */
  @Event() nanoImgDidLoad!: EventEmitter<void>;

  /** Emitted when the img fails to load */
  @Event() nanoImgError!: EventEmitter<void>;

  private addIO() {
    if (!this._src || this.hasLoaded) return;
    if (!this.lazy) {
      this.load();
      return;
    }
    if (
      typeof (window as any) !== 'undefined' &&
      'IntersectionObserver' in window &&
      'isIntersecting' in window.IntersectionObserverEntry.prototype
    ) {
      this.removeIO();
      this.io = new IntersectionObserver((data) => {
        if (data[0].isIntersecting) {
          this.load();
          this.removeIO();
        }
      });

      this.io.observe(this.host);
    } else setTimeout(() => this.load(), 200);
  }

  private load() {
    this.loadError = this.onError;
    this.loadSrc = this._src;
    this.nanoImgWillLoad.emit();
  }

  private onLoad = () => {
    this.nanoImgDidLoad.emit();
    setTimeout(() => (this.hasLoaded = true), 50);
  };

  private onError = () => {
    this.nanoImgError.emit();
  };

  private onResize = (e: { detail: ResizeStateChangeEventDetail }) => {
    Object.entries(e.detail).forEach(([bp, active]) => {
      this._srcSet[bp].active = active;
    });

    // sort and find the highest sized matching image.
    const srcOpts = [];
    Object.keys(this._srcSet)
      .sort()
      .forEach((bp) => {
        if (this._srcSet[bp].active) srcOpts.push(this._srcSet[bp].src);
      });

    if (srcOpts.length) this._src = srcOpts.slice(-1)[0];
    // no matching image? Set back to default
    else this._src = this.src;
  };

  private removeIO() {
    if (this.io) {
      this.io.disconnect();
      this.io = undefined;
    }
  }

  connectedCallback() {
    this.srcChanged();
    this.srcSetChanged();
    this.lazyChanged();
  }

  componentDidLoad() {
    this.addIO();
  }

  disconnectedCallback() {
    this.removeIO();
  }

  render() {
    const bgStyle = !!this.loadSrc
      ? { 'background-image': `url(${this.loadSrc})` }
      : {};
    return (
      <div class="img">
        <nano-skeleton class="img__loader"></nano-skeleton>
        {!!this.background && (
          <div
            class={{
              loaded: this.hasLoaded,
              img__bg: true,
              'no-height': this.autoHeight === 'image',
            }}
            style={bgStyle}
          >
            <slot />
          </div>
        )}
        <img
          class={{
            img__image: true,
            loaded: this.hasLoaded,
            hide: this.background,
            'no-height': this.autoHeight === 'content',
          }}
          decoding="async"
          src={this.loadSrc}
          alt={this.alt}
          onLoad={this.onLoad}
          onError={this.loadError}
        />
        <nano-resize-observe
          class="img__observer"
          onNanoResizeStateChange={this.onResize}
          states={this.imgStates}
        />
      </div>
    );
  }
}
